
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import type { CompileResult } from '@/types';
import { cn } from '@/lib/utils';

interface CompileOutputProps {
  result: CompileResult;
  onErrorClick?: (file: string, start: number) => void;
}

export default function CompileOutput({ result, onErrorClick }: CompileOutputProps) {
  if (result.success && result.errors.length === 0 && result.warnings.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded">
        <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
        <div>
          <p className="text-sm text-green-300 font-medium">
            Compilation successful
          </p>
          {result.contractName && (
            <p className="text-xs text-green-400/70 mt-0.5">
              Contract: {result.contractName}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Errors */}
      {result.errors.map((err, i) => (
        <div
          key={`err-${i}`}
          className={cn(
            'flex items-start gap-2 p-2 rounded text-xs bg-red-500/10 border border-red-500/20',
            err.sourceLocation && 'cursor-pointer hover:bg-red-500/15'
          )}
          onClick={() => {
            if (err.sourceLocation && onErrorClick) {
              onErrorClick(err.sourceLocation.file, err.sourceLocation.start);
            }
          }}
        >
          <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-red-300">{err.message}</p>
            {err.sourceLocation && (
              <p className="text-red-400/60 mt-0.5">
                {err.sourceLocation.file}:{err.sourceLocation.start}
              </p>
            )}
          </div>
        </div>
      ))}

      {/* Warnings */}
      {result.warnings.map((warn, i) => (
        <div
          key={`warn-${i}`}
          className={cn(
            'flex items-start gap-2 p-2 rounded text-xs bg-amber-500/10 border border-amber-500/20',
            warn.sourceLocation && 'cursor-pointer hover:bg-amber-500/15'
          )}
          onClick={() => {
            if (warn.sourceLocation && onErrorClick) {
              onErrorClick(warn.sourceLocation.file, warn.sourceLocation.start);
            }
          }}
        >
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-300">{warn.message}</p>
            {warn.sourceLocation && (
              <p className="text-amber-400/60 mt-0.5">
                {warn.sourceLocation.file}:{warn.sourceLocation.start}
              </p>
            )}
          </div>
        </div>
      ))}

      {/* Success with warnings */}
      {result.success && result.errors.length === 0 && (
        <div className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/20 rounded">
          <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
          <p className="text-xs text-green-300">Compiled with warnings</p>
        </div>
      )}

      {/* Raw compiler output — useful for debugging */}
      {(result.stdout || result.stderr) && (
        <details className="group mt-1">
          <summary className="text-[10px] text-gray-500 cursor-pointer hover:text-gray-300">
            Raw output {result.durationMs ? `(${result.durationMs}ms)` : ''}
          </summary>
          <pre className="mt-1 text-[10px] text-gray-500 bg-[#1e1e1e] p-2 rounded max-h-24 overflow-auto whitespace-pre-wrap">
            {[result.stdout, result.stderr].filter(Boolean).join('\n')}
          </pre>
        </details>
      )}
    </div>
  );
}
